import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import CommonTable from "../../../components/Common/TableContainer";
import AIFadvPackageAddEditModal from "pages/AllCommonModal/AIFadvPackageAddEditModal/add-edit-ai-fadv-package-modal";
import { submitFilterData } from "model/AIFadvPackageModel";
import { useAIFadvPackageContext } from "libs/context/AIFadvPackageContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";


const AIFadvPackage = () => {
  const { permissionData } = usePermissionContext() || {};
  const {
    fetchAIFadvPackageContextData,
    aiFadvPackageContextData,
  } = useAIFadvPackageContext() || {};
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("FADV Api Package");
  const initialFilterFetchedRef = useRef(false);
  const [aiFadvPackageAddEditModalOpen, setAIFadvPackageAddEditModalOpen] =
    useState(false);
  const [modal1, setModal1] = useState(false);
  const toggleViewModal = () => setModal1(!modal1);
  const [searchQuery, setSearchQuery] = useState("");
  const [aiFadvPackageData, setAIFadvPackageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemId, setItemId] = useState(null);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const initialState = {
    fadvTitle: "",
    buttonLabel: "",
    fadvPackage: "",
    fadvPosition: "",
    fadvDriver: "",
    fadvPackageType: "",
  };

  const [formData, setFormData] = useState(initialState);

  const filteredData = useMemo(() => aiFadvPackageData, [aiFadvPackageData]);

  const aiFadvPackageActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.JobAddAccess),
      canEdit: Boolean(permissionData?.JobUpdateAccess),
    }),
    [permissionData]
  );

  const hasAnyAIFadvPackageRowAction = useMemo(
    () => aiFadvPackageActionPermissions.canEdit,
    [aiFadvPackageActionPermissions]
  );

  const handleEdit = (row) => {
    setItemId(row?.id);
    setAIFadvPackageAddEditModalOpen(true);
  };

  useEffect(() => {
    fetchAIFadvPackageContextData();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getAIFadvPackageList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const valueReModified = aiFadvPackageContextData?.table?.columns?.map(
      (col) => ({
        Header: col?.title,
        accessor: col?.field,
      })
    );
    setTableMetadata(valueReModified);
  }, [aiFadvPackageContextData]);

  const getAIFadvPackageList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = {
      ...requestPayload,
      ...payloadOverrides,
    };
    const response = await submitFilterData(payload);
    const responseData = response?.data || {};
    const listData =
      responseData?.records ??
      (Array.isArray(responseData) ? responseData : []);
    const valueReModified = (Array.isArray(listData) ? listData : []).map(
      (item) => ({
        ...item,
        ID: item?.id,
      })
    );

    if (response) {
      setAIFadvPackageData(valueReModified);
      setPaginationMeta({
        pageNumber:
          Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize:
          Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords:
          Number(responseData?.totalRecords) || valueReModified.length,
      });
      setRequestPayload(payload);
    } else {
      setAIFadvPackageData([]);
    }
    setLoading(false);
  };

  const handleFilterSubmit = async (data) => {
    await getAIFadvPackageList({
      ...data,
      search: requestPayload?.search || "",
      pageNumber: 1,
    });
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    const trimmedValue = value?.trim() || "";
    await getAIFadvPackageList({
      ...requestPayload,
      pageNumber: 1,
      search: trimmedValue,
    });
  };

  const handlePageChange = async (page) => {
    await getAIFadvPackageList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getAIFadvPackageList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">FADV Api Package</h5>

                {aiFadvPackageActionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button
                      color="primary"
                      onClick={() => {
                        setItemId(null);
                        setAIFadvPackageAddEditModalOpen(true);
                        setFormData(initialState);
                      }}
                    >
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => {
                        const nextValue = e?.target?.value || "";
                        setSearchQuery(nextValue);
                        if (!nextValue.trim()) {
                          handleSearchSubmit("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyAIFadvPackageRowAction}
                  onEdit={
                    aiFadvPackageActionPermissions.canEdit
                      ? handleEdit
                      : undefined
                  }
                  isEditAccess={aiFadvPackageActionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"FADV Api Package"}
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <AIFadvPackageAddEditModal
            setAIFadvPackageAddEditModalOpen={setAIFadvPackageAddEditModalOpen}
            aiFadvPackageAddEditModalOpen={aiFadvPackageAddEditModalOpen}
            getAIFadvPackageList={getAIFadvPackageList}
            itemId={itemId}
            setAIFadvPackageData={setAIFadvPackageData}
            setFormData={setFormData}
            formData={formData}
            totalData={aiFadvPackageData}
          />
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="FADV Api Package"
            handleSubmit={handleFilterSubmit}
            filterData={aiFadvPackageContextData?.filters}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

AIFadvPackage.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AIFadvPackage);
